import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import type { SetStateAction } from "react";
import hark from "hark";
import { useAtom } from "jotai";
import { SkyWayStreamFactory } from "@skyway-sdk/core";

import {
  body,
  bodyExpand,
  bodyExtendFront,
  switchIcon,
  switchIconWrap,
  settings,
  myname,
  video,
  cameraoff,
  cameraoffExpand,
  cameraoffExtendFront,
  avatorWrap,
  avatorWrapExpand,
  avatorWrapNoBorder,
  avator,
  avatorName,
} from "./style";
import { CameraButton } from "components/CameraButton";
import { MicButton } from "components/MicButton";
import { MicCameraSettings } from "components/MicCameraSettings";
import { SettingButton } from "components/SettingButton";
import { SettingModal } from "components/SettingModal";
import { Spacer } from "components/Spacer";
import { Icon } from "icons/Icon";
import {
  myStreamAtom,
  myCameraAtom,
  useMyVideoAtom,
  remoteAudioPublicationAtom,
  remoteVideoPublicationAtom,
  currentAudioDeviceIdAtom,
  currentVideoDeviceIdAtom,
} from "states";
import { DeviceTypes, myVideoHandler, myVideoProps } from "types";
import { Color } from "utils/variables";

const ledColor = "#8AD670";
// 自分のストリーミング情報を保存しておく変数
let localStream: MediaStream;

export const MyVideo = forwardRef<myVideoHandler, myVideoProps>(
  (
    { isExpand, isFront, handleParentMic, handleParentCamera, handleFront },
    ref
  ) => {
    const [onMic, setMic] = useState<boolean>(true);
    const [onCamera, setCamera] = useAtom(myCameraAtom);
    const [cameraType, setCameraType] = useState<"in" | "out">("in");
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [remoteAudioPublication] = useAtom(remoteAudioPublicationAtom);
    const [remoteVideoPublication] = useAtom(remoteVideoPublicationAtom);
    const [, setCurrentAudioDeviceId] = useAtom(currentAudioDeviceIdAtom);
    const [, setCurrentVideoDeviceId] = useAtom(currentVideoDeviceIdAtom);
    const [, setMyStream] = useAtom(myStreamAtom);
    const [videoDevices, setVideoDevices] = useState<DeviceTypes[]>([]);
    const [audioDevices, setAudioDevices] = useState<DeviceTypes[]>([]);
    const [useMyVideo] = useAtom(useMyVideoAtom);
    // 自分のビデオを入れる要素
    const myVideo = useRef<HTMLVideoElement>(null);

    // 自分の映像を取得
    useEffect(() => {
      if (!useMyVideo) return;
      const devicesProcess = async () => {
        navigator.mediaDevices
          .getUserMedia({
            video: true,
            audio: true,
          })
          .then(async (stream) => {
            if (myVideo.current !== null) {
              myVideo.current.srcObject = stream;
            }
            localStream = stream;
            // カメラの初期をオフにする
            localStream.getVideoTracks()[0].enabled = false;
            handleParentCamera(false);
            setCamera(false);
            setMyStream(localStream);
            // audioとvideoのデバイス一覧取得
            const audio = await devices("audioinput");
            const video = await devices("videoinput");
            setAudioDevices(audio);
            setVideoDevices(video);
          })
          .catch((error) => {
            console.error(error);
          });
      };
      devicesProcess();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useMyVideo]);

    // 自分の音量を拾う
    useEffect(() => {
      if (localStream?.getAudioTracks().length === 0 || !localStream) return;

      const harker = hark(localStream, { threshold: -75 });
      harker.on("volume_change", (db) => {
        if (db === -Infinity) {
          db = 0;
        } else {
          // dbを -100 ~ 0 (silent ~ loud)の間でキャストして、値の大小を反転
          if (db < -100) db = -100;
          if (db > 0) db = 0;
          db = 100 - Math.abs(db);
        }
        leds(Math.abs(db / 10));
      });

      return () => harker.stop();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localStream]);

    // 親コンポーネントから呼ばれる関数
    useImperativeHandle(ref, () => ({
      openSettingModal() {
        setModalIsOpen(true);
      },
      handleMicMode() {
        handleMicMode();
      },
      handleCameraMode() {
        handleCameraMode();
      },
    }));

    useEffect(() => {
      if (!remoteAudioPublication) return;
      const handleRemoteAudio = async () => {
        try {
          onMic
            ? remoteAudioPublication.enable()
            : remoteAudioPublication.disable();
        } catch (error) {
          console.error("remoteAudioPublication error", error);
        }
      };
      handleRemoteAudio();
    }, [onMic, remoteAudioPublication]);

    useEffect(() => {
      if (!remoteVideoPublication) return;
      const handleRemoteVideo = async () => {
        try {
          onCamera
            ? remoteVideoPublication.enable()
            : remoteVideoPublication.disable();
        } catch (error) {
          console.error("remoteVideoPublication error", error);
        }
      };
      handleRemoteVideo();
    }, [onCamera, remoteVideoPublication]);

    // マイクON/OFF
    const handleMicMode = () => {
      localStream.getAudioTracks()[0].enabled = !onMic;

      handleParentMic(!onMic);
      setMic(!onMic);
    };
    // カメラON/OFF
    const handleCameraMode = () => {
      localStream.getVideoTracks()[0].enabled = !onCamera;
      if (onCamera) {
        // カメラをオフにした時、デバイスのカメラ接続を解除
        // localStream.getVideoTracks()[0].stop()
        // if (myVideo.current !== null) {
        //   myVideo.current.srcObject = null
        // }
        // setMyStream(localStream)
      } else {
        // カメラをオンにした時、デバイスのカメラ接続を復帰
        // .getVideoTracks()[0].start()みたいなものが存在せず新規で作成する
        const selectedAudioDevice = getSelectedDevices(audioDevices);
        const selectedVideoDevice = getSelectedDevices(videoDevices);
        navigator.mediaDevices
          .getUserMedia({
            video: selectedVideoDevice?.value
              ? { deviceId: { exact: selectedVideoDevice.value } }
              : true,
            audio: selectedAudioDevice?.value
              ? { deviceId: { exact: selectedAudioDevice.value } }
              : true,
          })
          .then(async (stream) => {
            // 一度localStreamを止めて新規のstreamに上書き
            stopTracks();
            localStream = stream;
            // 新規作成で初期化されているので、マイクの状態をセット
            localStream.getAudioTracks()[0].enabled = onMic;
            if (myVideo.current !== null) {
              myVideo.current.srcObject = localStream;
            }
            setMyStream(localStream);
          })
          .catch((error) => {
            console.error(error);
          });
      }
      handleParentCamera(!onCamera);
      setCamera(!onCamera);
    };

    // 前面/背面カメラの切り替え
    const changeCameraType = async (
      changeTo: "in" | "out",
      e: React.MouseEvent<HTMLElement, MouseEvent> | undefined
    ) => {
      e?.stopPropagation();
      // ビデオがオフの場合何もしない
      if (!onCamera) return;

      // 既に開いているカメラ/audioを閉じる (重要。これをしないと新しいカメラ/audioが開けない)
      localStream.getAudioTracks()[0].stop();
      localStream.getVideoTracks()[0].stop();

      const devices = await SkyWayStreamFactory.enumerateInputVideoDevices();
      console.log("devices", devices);

      const facingMode: ConstrainDOMString =
        changeTo === "in" ? "user" : { exact: "environment" };
      navigator.mediaDevices
        .getUserMedia({
          video: { facingMode },
          audio: true,
        })
        .then(async (stream) => {
          // ミュートしている場合、デフォルト値をミュートにする
          if (!onMic) stream.getAudioTracks()[0].enabled = false;
          // カメラタイプ(in/out)を変更
          setCameraType(changeTo);
          const currentVideoDevice = devices.find((device) => {
            return device.label === stream.getVideoTracks()[0].label;
          });
          const currentVideoStream =
            await SkyWayStreamFactory.createCameraVideoStream({
              deviceId: currentVideoDevice?.id,
            });
          console.log("current video stream", currentVideoStream);
          // setLocalVideoStream(currentVideoStream);
          remoteVideoPublication?.replaceStream(currentVideoStream);
          if (myVideo.current !== null) {
            myVideo.current.srcObject = stream;
          }
          localStream = stream;
          setMyStream(localStream);
        })
        .catch((error) => {
          // デバイスにアウトカメラがない場合のエラー (PCなど)
          if (error.name === "OverconstrainedError") {
            console.error("お使いの端末ではこの機能をお使いいただけません");
            changeCameraType("in", undefined); // インカメラで接続し直し
          }
        });
    };

    // マイクカメラ設定モーダル
    const settingModal = () => {
      setModalIsOpen(true);
    };

    // デバイス一覧取得
    const devices = async (type: string) => {
      const getDevices = await navigator.mediaDevices.enumerateDevices();
      const devicesKindFilter = getDevices.filter((device) => {
        return device.kind === type;
      });
      const deviceMap = devicesKindFilter.map((device) => {
        const labelText = device.label;
        let switchChecked;
        if (type === "audioinput") {
          switchChecked =
            localStream.getAudioTracks()[0]?.label === device.label
              ? true
              : false;
        } else {
          switchChecked =
            localStream.getVideoTracks()[0]?.label === device.label
              ? true
              : false;
        }

        return {
          text: labelText,
          value: device.deviceId,
          checked: switchChecked,
        } as DeviceTypes;
      });
      return deviceMap;
    };

    // オーディオのラジオボタンがチェンジされた時
    const onChangeAudioDevice = async (event: {
      target: { value: SetStateAction<string> };
    }) => {
      const afterDevice = audioDevices.map((device) => {
        return {
          text: device.text,
          value: device.value,
          checked: device.value === event.target.value ? true : false,
        } as DeviceTypes;
      });
      await setAudioDevices(afterDevice);
      setCurrentAudioDeviceId(String(event.target.value));
      await audioSwitching(String(event.target.value));
      if (myVideo.current !== null) {
        myVideo.current.srcObject = localStream;
      }
    };
    // ビデオのラジオボタンがチェンジされた時
    const onChangeVideoDevice = async (event: {
      target: { value: SetStateAction<string> };
    }) => {
      const afterDevice = videoDevices.map((device) => {
        return {
          text: device.text,
          value: device.value,
          checked: device.value === event.target.value ? true : false,
        } as DeviceTypes;
      });
      setVideoDevices(afterDevice);
      setCurrentVideoDeviceId(String(event.target.value));
      await videoSwitching(String(event.target.value));
      if (myVideo.current !== null) {
        myVideo.current.srcObject = localStream;
      }
    };

    // audioの切り替え
    const audioSwitching = async (audioSource: string) => {
      // トラック停止
      stopTracks();
      const selected = getSelectedDevices(videoDevices);
      const video = selected ? selected.value : "";

      const constraints: MediaStreamConstraints = {
        audio: {
          deviceId: audioSource,
        },
        video: {
          deviceId: video,
        },
      };
      const currentAudioStream =
        await SkyWayStreamFactory.createMicrophoneAudioStream({
          deviceId: audioSource,
        });
      return navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          gotStream(stream);
          remoteAudioPublication?.replaceStream(currentAudioStream);
        })
        .catch(handleError);
    };
    // カメラの切り替え
    const videoSwitching = async (videoSource: string) => {
      // トラック停止
      stopTracks();
      const selected = getSelectedDevices(audioDevices);
      const audio = selected ? selected.value : "";
      const constraints: MediaStreamConstraints = {
        audio: {
          deviceId: audio,
        },
        video: {
          deviceId: videoSource,
        },
      };
      const currentVideoStream =
        await SkyWayStreamFactory.createCameraVideoStream({
          deviceId: videoSource,
        });
      return navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          gotStream(stream);
          remoteVideoPublication?.replaceStream(currentVideoStream);
        })
        .catch(handleError);
    };

    // 音量でスタイルを当てる
    const leds = (vol: number) => {
      // sLteadgse→sdkでサイトとクラス名が被らないように調整
      // stage Led のアナグラムで生成
      const leds = [
        ...(document.getElementsByClassName(
          "volume-sLteadgse"
        ) as HTMLCollectionOf<HTMLElement>),
      ];
      const range = leds.slice(0, Math.round(vol));

      for (let i = 0; i < leds.length; i++) {
        leds[i]!.style.background = `${Color.LightGrey}`;
      }

      for (let i = 0; i < range.length; i++) {
        range[i]!.style.background = ledColor;
      }

      // sbeolradeegrde→sdkでサイトとクラス名が被らないように調整
      // stage led border のアナグラムで生成
      const level = [
        ...(document.getElementsByClassName(
          "my-sbeolradeegrde"
        ) as HTMLCollectionOf<HTMLElement>),
      ];
      if (level.length) {
        if (vol > 4) {
          for (let i = 0; i < level.length; i++) {
            level[i].style.borderColor = `${Color.PaleGreen}`;
          }
        } else {
          for (let i = 0; i < level.length; i++) {
            level[i].style.borderColor = `${Color.White}`;
          }
        }
      }
    };

    // トラック停止処理
    const stopTracks = () => {
      if (!localStream) return;
      localStream.getTracks().forEach((track) => track.stop());
      setMyStream(localStream);
    };

    // getUserMedia で MediaStream が取得できた際に実行する関数
    const gotStream = (stream: MediaStream) => {
      stream.getAudioTracks()[0].enabled = onMic;
      stream.getVideoTracks()[0].enabled = onCamera;
      localStream = stream;
      setMyStream(localStream);
    };
    // Promise の処理で reject された際に実行する関数
    const handleError = (error: Error) => {
      console.error(error.name, error.message);
    };

    // 現在選択されているデバイス取得
    const getSelectedDevices = (devices: DeviceTypes[]) => {
      const selected = devices.filter((device) => {
        return device.checked;
      });
      return selected.length === 1 ? selected[0] : undefined;
    };

    // 小画面をクリックした時
    const clickBody = () => {
      if (isExpand && isFront) handleFront();
    };

    return (
      <>
        <div
          className={`
          ${body}
          ${isExpand ? ` ${bodyExpand}` : ""}
          ${isExpand && isFront ? ` ${bodyExtendFront} my-sbeolradeegrde` : ""}
        `}
          onClick={() => clickBody()}
        >
          {onCamera && (
            <video ref={myVideo} autoPlay muted playsInline className={video} />
          )}
          {!onCamera && (
            <div
              className={`
              ${cameraoff}
              ${isExpand ? ` ${cameraoffExpand}` : ""}
              ${isExpand && isFront ? ` ${cameraoffExtendFront}` : ""}
            `}
            >
              <div
                className={`
                ${avatorWrap}
                ${isExpand ? ` ${avatorWrapExpand}` : ""}
                ${
                  isExpand && isFront
                    ? ` ${avatorWrapNoBorder}`
                    : " my-sbeolradeegrde"
                }
              `}
              >
                {isExpand ? (
                  <Icon name="Smile" size={57} className={avator} />
                ) : (
                  <Icon name="Smile" size={39} className={avator} />
                )}
              </div>
              {isExpand && !isFront && (
                <>
                  <Spacer size={15} />
                  <p className={avatorName}>あなた</p>
                </>
              )}
            </div>
          )}
          <div
            className={switchIconWrap}
            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
              changeCameraType(cameraType === "in" ? "out" : "in", e)
            }
          >
            <Icon name="Turn" size={28} className={switchIcon} />
          </div>
          <div className={settings}>
            {!isExpand && (
              <>
                <SettingButton onClick={() => settingModal()} />
                <Spacer size={10} />
                <MicButton onClick={() => handleMicMode()} on={onMic} />
                <Spacer size={10} />
                <CameraButton
                  onClick={() => handleCameraMode()}
                  on={onCamera}
                />
              </>
            )}
          </div>
          {!(isExpand && !isFront) && <p className={myname}>あなた</p>}
        </div>
        <SettingModal
          isOpen={modalIsOpen}
          onRequestClose={() => setModalIsOpen(false)}
          label="Settings"
        >
          <MicCameraSettings
            audioDevices={audioDevices}
            videoDevices={videoDevices}
            onChangeAudioDevice={onChangeAudioDevice}
            onChangeVideoDevice={onChangeVideoDevice}
          />
        </SettingModal>
      </>
    );
  }
);
